import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Swal from 'sweetalert2';
import { FormLabel, FormControlLabel, Checkbox } from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import request from '../../services/Request';
import '../user/form.css';
import { Modal } from 'react-bootstrap';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const updateAlert = '/machines/updateEmailAlert';

export default function AlertModal(props) {
  const {
    reset,
    handleSubmit,
    register,
    setError,
    control,
    formState: { errors },
  } = useForm({});
  const classes = useStyles();
  const { data, user, setMachines, setRowData, ...rest } = props;
  const showSecondEmail = user.fullAccess && user.dba;

  const [loading, setLoading] = useState(true);

  const defaultValues = {
    alertEmail: data[0].alertEmail ? data[0].alertEmail : '',
    alertEmail2: data[0].alertEmail2 ? data[0].alertEmail2 : '',
    disableOfflineAlert: data[0].disable_alert_setting?.offline || false,
    enableOnlineAlert: data[0].alert_settings?.onlineAlert || false,
    enableHourlyOfflineAlert: data[0].alert_settings?.hourlyOfflineAlert || false,
  };

  useEffect(async () => {
    if (props.show) {
      reset(defaultValues);
    }
  }, [props.show]);

  const onSubmit = async (formData) => {
    if (formData.alertEmail.split(',').length > 5) {
      setError('alertEmail', {
        message: 'Maximum 5 email address',
      });
    } else {
      if (showSecondEmail) {
        if (formData.alertEmail2.split(',').length > 5) {
          setError('alertEmail2', {
            message: 'Maximum 5 email address',
          });
        }
      }
      setLoading(false);

      const machineID = data.map((e) => e._id);

      await request({
        method: 'post',
        url: updateAlert,
        data: {
          machineID: machineID,
          alertEmail: formData.alertEmail.trim(),
          alertEmail2: formData?.alertEmail2?.trim(),
          disable_alert_setting: {
            offline: formData.disableOfflineAlert,
          },
          alert_settings: {
            onlineAlert: formData.enableOnlineAlert,
            hourlyOfflineAlert: formData.enableHourlyOfflineAlert,
          },
        },
      })
        .then((res) => {
          setLoading(true);

          setMachines((prevMachines) =>
            prevMachines.map((e) =>
              machineID.includes(e._id)
                ? {
                    ...e,
                    alertEmail: formData.alertEmail.trim(),
                    alertEmail2: formData?.alertEmail2?.trim(),
                    disable_alert_setting: {
                      offline: formData.disableOfflineAlert,
                    },
                    alert_settings: {
                      onlineAlert: formData.enableOnlineAlert,
                      hourlyOfflineAlert: formData.enableHourlyOfflineAlert,
                    },
                  }
                : e
            )
          );

          setRowData((prevRowData) =>
            prevRowData.map((e) => ({
              ...e,
              alertEmail: formData.alertEmail.trim(),
              alertEmail2: formData?.alertEmail2?.trim(),
              disable_alert_setting: {
                offline: formData.disableOfflineAlert,
              },
              alert_settings: {
                onlineAlert: formData.enableOnlineAlert,
                hourlyOfflineAlert: formData.enableHourlyOfflineAlert,
              },
            }))
          );

          Swal.fire({
            text: `Your changes has been updated!`,
            icon: 'success',
          }).then(() => {
            props.onHide();
          });
        })
        .catch((error) => {
          setLoading(true);
          Swal.fire({
            text: error?.response?.data?.message || 'Failed to update alert email',
            icon: 'error',
          });
        });
    }
  };

  return (
    <Modal className="modal-w" {...rest} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter" style={{ fontSize: '1.2rem' }}>
          Update Settings
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <div>
            <form className={classes.form} onSubmit={handleSubmit(onSubmit)} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12} style={{ textAlignLast: 'center' }}>
                  <FormLabel component="legend">
                    Email address for alert (To use more than one email address, separate the
                    addresses with comma, up to 5 email adresses. Example:
                    johndoe@ae.com,william@ba.com,smith.peter@olk.com)
                  </FormLabel>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Email"
                    {...register('alertEmail', {})}
                  />
                  {errors.alertEmail && (
                    <p className="signUpErrorMessage">{errors.alertEmail.message}</p>
                  )}
                </Grid>
                {showSecondEmail && (
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label="Hidden Email"
                      {...register('alertEmail2', {})}
                    />
                    {errors.alertEmail && (
                      <p className="signUpErrorMessage">{errors.alertEmail2.message}</p>
                    )}
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Controller
                    name="disableOfflineAlert"
                    control={control}
                    defaultValue={defaultValues.disableOfflineAlert}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Disable offline alert"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="enableOnlineAlert"
                    control={control}
                    defaultValue={defaultValues.enableOnlineAlert}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Alert when machine comes back online"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="enableHourlyOfflineAlert"
                    control={control}
                    defaultValue={defaultValues.enableHourlyOfflineAlert}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Send offline alert every hour"
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!loading}
                color="primary"
                className={classes.submit}
              >
                Update
              </Button>
            </form>
          </div>
        </Container>
      </Modal.Body>
    </Modal>
  );
}
